import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useRosterStatusTypeList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refRosterStatusTypesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'name',
      label: 'status type',
      sortable: true,
    },
    {
      key: 'abbreviation',
      label: 'ABBRV.',
      sortable: true,
    },
    {
      key: 'allowAssignment',
      label: 'allow assignments',
      sortable: false,
    },
    {
      key: 'remarks',
      label: 'Remarks',
      sortable: false,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalRosterStatusTypes = ref(0)
  const searchQuery = ref(route.query.search || '')
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const sortBy = ref(route.query.sortBy ?? 'name')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref(route.query.status ?? null)
  const assignmentFilter = ref(route.query.assignment ?? null)
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refRosterStatusTypesTable.value ? refRosterStatusTypesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRosterStatusTypes.value,
    }
  })

  const refetchData = () => {
    refRosterStatusTypesTable.value.refresh()
  }

  watch([searchQuery, statusFilter, assignmentFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchRosterStatusTypes = (ctx, callback) => {
    store
      .dispatch('app-roster-status-types/fetchRosterStatusTypes', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        assignment: assignmentFilter.value,
      },
      { root: true })
      .then(response => {
        const { rosterStatusTypes, total } = response.data
        currentDataLength.value = rosterStatusTypes.length
        callback(rosterStatusTypes)
        totalRosterStatusTypes.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              assignment: assignmentFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteRosterStatusType = () => {
    store
      .dispatch('app-roster-status-types/deleteRosterStatusType', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveRosterStatusTypeStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'danger'
    return 'primary'
  }

  const resolveAssignmentText = assignment => {
    if (assignment === 'yes') return 'Allow Assignment'
    if (assignment === 'show warning') return 'Show Warning'
    return 'Show Warning'
  }

  return {
    fetchRosterStatusTypes,
    tableColumns,
    perPage,
    currentPage,
    totalRosterStatusTypes,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refRosterStatusTypesTable,
    searchQuery,

    resolveRosterStatusTypeStatusVariant,
    resolveAssignmentText,
    refetchData,
    currentDataLength,

    toDeletedID,
    deleteRosterStatusType,
    statusFilter,
    assignmentFilter,
  }
}
